import i18next from 'i18next';
import { get, isEmpty, noop, values } from 'lodash/fp';
import { Defaults, QueryState } from 'app/lib/constants';
import { quoteIfNeeded, toStringFqn } from 'app/lib/utils';
import { isDatasetsList } from 'app/routes/routes.js';
import { sql, select, from, identifier, limit } from 'app/lib/utils/sql_generator.js';
import {
    CANCEL_ALL_QUERIES_OPERATION,
    CANCEL_SNOWFLAKE_QUERY_OPERATION,
    GET_SNOWFLAKE_SQL_BY_PROJECT_SQL_OPERATION,
    GET_SNOWFLAKE_SQL_BY_TRANSFORMATION_ID_OPERATION,
    GET_SNOWFLAKE_SQL_BY_TRANSFORMATION_OPERATION,
    GET_SNOWFLAKE_SQL_FOR_OUTPUT_MAPPING_OPERATION,
    LIST_SNOWFLAKE_QUERIES_OPERATION
} from 'app/store/__generated__/operations';
import {
    selectCurrentViz,
    selectQueriesByEntityId,
    selectVizOptions
} from 'app/store/exploreData/selectors.js';
import { doChooseViz } from 'app/store/exploreData/actions.js';
import { selectEntityByPropId } from 'app/store/entities/selectors.js';
import { selectCurrentFqn } from 'app/store/findDatasets/selectors.js';
import { selectProject, selectProjectUid } from 'app/store/projects/selectors.js';
import { QueryKeys } from './reducer.js';
import {
    CANCEL_QUERY,
    CANCEL_ALL_QUERIES,
    DELETE_DATASET_QUERY,
    DELETE_JOIN_QUERIES,
    DELETE_EXPLORE_QUERY,
    DELETE_FQN_QUERY,
    DELETE_EXTRACT_JSON_QUERIES,
    ENSURE_DATASET_LOADED,
    EXECUTE_SQL_QUERY,
    EXECUTE_DATASET_QUERY,
    EXECUTE_TRANSFORMATION_QUERY,
    EXECUTE_EXPLORE_QUERY,
    EXECUTE_FQN_QUERY,
    EXECUTE_ROW_COUNT_QUERY,
    INVALIDATE_FQN_QUERY,
    LOOKUP_AND_CANCEL_QUERY,
    CLEANUP_AND_INVALIDATE_QUERIES,
    INVALIDATE_EXPLORE_QUERIES,
    DELETE_ROW_COUNT_QUERY,
    LIST_QUERIES,
    RESUME_QUERY,
    RESTORE_QUERY,
    DELETE_FILTERED_METRICS_QUERIES,
    CANCEL_AND_DELETE_EXPLORE_QUERIES,
    DELETE_QUERY,
    EXECUTE_COLUMN_MANAGEMENT_PREVIEW_QUERY,
    DELETE_MULTIJOIN_VALIDATION_QUERIES
} from './types.js';
import {
    selectSlice,
    selectExploreQueriesForPropEntityId,
    selectQueries,
    selectQueryForPropEntityId,
    selectQueryForPropKey,
    selectRunningQueriesMetadatasForPropKey
} from './selectors.js';
import { previewSql } from './utils.js';

export const doCancelQuery = ({ id, queryKey = undefined }) => ({
    type: CANCEL_QUERY,
    api: { ...CANCEL_SNOWFLAKE_QUERY_OPERATION, sync: true },
    params: { id },
    ...(queryKey && { meta: { queryKey } })
});

export const doCancelAllQueries = () => ({
    type: CANCEL_ALL_QUERIES,
    api: CANCEL_ALL_QUERIES_OPERATION
});

export const doLookupAndCancelQuery = (queryKey) => ({
    type: LOOKUP_AND_CANCEL_QUERY,
    immediateEffect: ({ store }) => {
        const state = store.getState();
        const query = selectQueryForPropKey(state, { queryKey });

        if (query?.state === QueryState.RUNNING && query.queryId) {
            store.dispatch(doCancelQuery({ id: query.queryId, queryKey }));
        }
    }
});

export const doExecuteDatasetQuery = ({
    entityId,
    fullData = false,
    limit = 100,
    onSucceeded = noop
}) => ({
    type: EXECUTE_DATASET_QUERY,
    queryKey: `byEntityId['${entityId}']`,
    getSqlParams: {
        ...GET_SNOWFLAKE_SQL_BY_TRANSFORMATION_ID_OPERATION,
        transformationId: entityId,
        sampleInputs: !fullData,
        limit
    },
    entityIds: [entityId],
    meta: (state) => {
        const entity = selectEntityByPropId(state, { entityId });
        const project = selectProject(state);

        return {
            entityId,
            fullData,
            queryKey: `byEntityId['${entityId}']`,
            startedAt: new Date().toISOString(),
            description: i18next.t('queries:preview', { name: `${project.name} / ${entity?.name}` })
        };
    },
    context: {
        entityId,
        fullData,
        limit
    },
    onSucceeded
});

export const doExecuteTransformationQuery = ({
    queryKey,
    transformation,
    entityId,
    fullData = false,
    limit = 100,
    description
}) => ({
    type: EXECUTE_TRANSFORMATION_QUERY,
    queryKey,
    getSqlParams: {
        ...GET_SNOWFLAKE_SQL_BY_TRANSFORMATION_OPERATION,
        transformation,
        transformationId: entityId,
        sampleInputs: !fullData,
        limit
    },
    entityIds: [entityId],
    meta: {
        queryKey,
        fullData,
        startedAt: new Date().toISOString(),
        description
    },
    context: {
        queryKey,
        entityId,
        transformation,
        fullData,
        limit,
        description
    }
});

export const doExecuteSqlQuery = ({
    queryKey,
    sql,
    skipSqlTranslate = false,
    entityIds = [],
    spaceUid,
    limit,
    fullData,
    description,
    onSucceeded = Defaults.NOOP
}) => ({
    type: EXECUTE_SQL_QUERY,
    queryKey,
    getSqlParams: {
        ...GET_SNOWFLAKE_SQL_BY_PROJECT_SQL_OPERATION,
        spaceUid,
        sql,
        skipSqlTranslate,
        sampleInputs: !fullData,
        limit
    },
    entityIds,
    meta: {
        queryKey,
        fullData,
        startedAt: new Date().toISOString(),
        description,
        sql
    },
    context: {
        queryKey,
        sql,
        skipSqlTranslate,
        entityIds,
        spaceUid,
        limit,
        fullData,
        description
    },
    onSucceeded
});

export const doExecuteColumnManagementPreviewQuery = ({ uuid, mappings }) => ({
    type: EXECUTE_COLUMN_MANAGEMENT_PREVIEW_QUERY,
    queryKey: QueryKeys.COLUMN_MAPPING_PREVIEW,
    getSqlParams: (state) => {
        if (isEmpty(mappings)) {
            const entity = selectEntityByPropId(state, { entityId: uuid });

            return {
                ...GET_SNOWFLAKE_SQL_BY_PROJECT_SQL_OPERATION,
                spaceUid: entity.spaceUid,
                sql: sql(select('*'), from(identifier(entity.name))),
                sampleInputs: false,
                limit: 100
            };
        }

        return {
            ...GET_SNOWFLAKE_SQL_FOR_OUTPUT_MAPPING_OPERATION,
            uuid,
            mappings,
            limit: 100
        };
    },
    entityIds: [uuid],
    meta: (state) => {
        const entity = selectEntityByPropId(state, { entityId: uuid });

        return {
            queryKey: QueryKeys.COLUMN_MAPPING_PREVIEW,
            startedAt: new Date().toISOString(),
            description: i18next.t('queries:columnMapping.preview', { name: `${entity.name}` })
        };
    },
    context: {
        uuid,
        mappings
    }
});

export const doExecuteExploreQuery = ({
    spaceId,
    entityId,
    queryUuid,
    pathname,
    description,
    skipRowCount = false
}) => ({
    type: EXECUTE_EXPLORE_QUERY,
    queryKey: `byExploreQueryUuid['${queryUuid}']`,
    getSqlParams: (state) => {
        const queriesByEntityId = selectQueriesByEntityId(state);
        const { sql } = queriesByEntityId[entityId][queryUuid];

        return {
            ...GET_SNOWFLAKE_SQL_BY_PROJECT_SQL_OPERATION,
            spaceUid: spaceId,
            sql: skipRowCount ? sql : previewSql(sql),
            sampleInputs: false,
            limit: 1000
        };
    },
    entityIds: [entityId],
    meta: {
        entityId,
        queryUuid,
        spaceId,
        queryKey: `byExploreQueryUuid['${queryUuid}']`,
        pathname,
        description,
        startedAt: new Date().toISOString(),
        skipRowCount
    },
    context: (state) => {
        const queriesByEntityId = selectQueriesByEntityId(state);
        const query = queriesByEntityId[entityId][queryUuid];
        const entity = selectEntityByPropId(state, { entityId });

        return {
            type: 'EXPLORE',
            model: query.model,
            projectName: entity?.spaceName,
            viewName: entity?.name,
            source: entity?.tableFqn,
            spaceId,
            entityId,
            queryUuid,
            pathname,
            description,
            skipRowCount
        };
    },
    onSucceeded: ({ dispatch, getState, meta }) => {
        const state = getState();
        const { entityId } = meta;

        const vizOptions = selectVizOptions(state, { entityId, queryUuid });
        const currentViz = selectCurrentViz(state, { entityId, queryUuid });

        if (!vizOptions.includes(currentViz)) {
            dispatch(doChooseViz({ entityId, queryUuid, viz: 'table' }));
        }
    }
});

export const doExecuteFQNQuery = ({ fqn }) => ({
    type: EXECUTE_FQN_QUERY,
    queryKey: ['byFQN', `${toStringFqn(fqn)}`],
    getSqlParams: {
        sql: sql(select('*'), from(toStringFqn(fqn), limit(100))),
        skipSqlTranslate: true,
        sampleInputs: true
    },
    meta: {
        fqn,
        queryKey: ['byFQN', `${toStringFqn(fqn)}`],
        startedAt: new Date().toISOString(),
        description: i18next.t('queries:preview', { name: toStringFqn(fqn) })
    },
    context: {
        fqn
    }
});

export const doExecuteRowCountQuery = ({ fqn, entityId }) => ({
    type: EXECUTE_ROW_COUNT_QUERY,
    queryKey: `${QueryKeys.ROW_COUNT}['${fqn ?? entityId}']`,
    getSqlParams: (state) => {
        if (fqn) {
            const sql = `SELECT COUNT(*) FROM ${fqn}`;

            return {
                sql,
                skipSqlTranslate: true,
                sampleInputs: false
            };
        }

        const entity = selectEntityByPropId(state, { entityId });
        const spaceUid = selectProjectUid(state);
        const sql = `SELECT COUNT(*) FROM ${quoteIfNeeded(entity.name)}`;

        return {
            ...GET_SNOWFLAKE_SQL_BY_PROJECT_SQL_OPERATION,
            spaceUid,
            sql,
            sampleInputs: false
        };
    },
    meta: {
        fqnOrEntityId: fqn ?? entityId,
        queryKey: `${QueryKeys.ROW_COUNT}['${fqn ?? entityId}']`,
        startedAt: new Date().toISOString(),
        description: i18next.t('queries:rowCount')
    },
    context: {
        fqn,
        entityId
    }
});

export const doDeleteDatasetQuery = ({ entityId }) => ({
    type: DELETE_DATASET_QUERY,
    payload: { entityId }
});

export const doDeleteJoinQueries = () => ({
    type: DELETE_JOIN_QUERIES
});

export const doCancelAndDeleteMultiJoinValidationQueries = () => ({
    type: DELETE_MULTIJOIN_VALIDATION_QUERIES,
    immediateEffect: ({ store }) => {
        const slice = selectSlice(store.getState());
        const queries = [
            ...values(get(QueryKeys.MULTI_JOIN_MATCHEDROWS, slice)),
            ...values(get(QueryKeys.MULTI_JOIN_UNMATCHEDROWS, slice)),
            ...values(get(QueryKeys.MULTI_JOIN_MULTIPLICATION, slice))
        ];

        queries.forEach((query) => {
            store.dispatch(doLookupAndCancelQuery(query.meta.queryKey));
        });
    }
});

export const doDeleteExploreQuery = ({ queryUuid }) => ({
    type: DELETE_EXPLORE_QUERY,
    payload: { queryUuid }
});

export const doDeleteExtractJsonQueries = () => ({
    type: DELETE_EXTRACT_JSON_QUERIES
});

export const doDeleteFilteredMetricsQueries = () => ({
    type: DELETE_FILTERED_METRICS_QUERIES
});

export const doEnsureDatasetLoaded = ({ entityId, fullData = false, onSucceeded = noop }) => ({
    type: ENSURE_DATASET_LOADED,
    effect: ({ store }) => {
        const state = store.getState();
        const query = selectQueryForPropEntityId(state, { entityId });

        if (isEmpty(query) || query.state !== QueryState.SUCCESS || query.fullData !== fullData) {
            store.dispatch(doExecuteDatasetQuery({ entityId, fullData, onSucceeded }));
        }
    }
});

export const doDeleteRowCountQuery = ({ fqnOrId }) => ({
    type: DELETE_ROW_COUNT_QUERY,
    payload: { fqnOrId }
});

export const doDeleteFQNQuery = ({ fqn }) => ({
    type: DELETE_FQN_QUERY,
    payload: { fqn }
});

export const doDeleteQuery = (queryKey) => ({
    type: DELETE_QUERY,
    payload: { queryKey }
});

/**
 *
 * @param {null | SnowflakeTableFqn } providedFqn
 */
export const doInvalidateFQNQuery = (providedFqn = null) => ({
    type: INVALIDATE_FQN_QUERY,
    effect: ({ store }) => {
        let fqn = providedFqn;

        if (fqn === null) {
            const state = store.getState();
            fqn = selectCurrentFqn(state);
        }

        if (isDatasetsList() && !isEmpty(fqn)) {
            store.dispatch(doExecuteFQNQuery({ fqn }));
        } else {
            store.dispatch(doDeleteFQNQuery({ fqn }));
        }
    }
});

export const doInvalidateExploreQueries = (explorationQueries) => ({
    type: INVALIDATE_EXPLORE_QUERIES,
    payload: { explorationQueries }
});

export const doCleanupAndInvalidateQueries = (queryKey) => ({
    type: CLEANUP_AND_INVALIDATE_QUERIES,
    payload: { queryKey },
    immediateEffect: ({ store }) => {
        const state = store.getState();
        const runningQueriesMetadatas = selectRunningQueriesMetadatasForPropKey(state, {
            queryKey
        });
        for (const metadata of runningQueriesMetadatas) {
            if (metadata.queryId) {
                store.dispatch(doCancelQuery({ id: metadata.queryId }));
            }
        }
    }
});

export const doCancelAndDeleteExploreQueries = (entityId) => ({
    type: CANCEL_AND_DELETE_EXPLORE_QUERIES,
    immediateEffect: ({ store }) => {
        const state = store.getState();
        const queries = selectExploreQueriesForPropEntityId(state, { entityId });

        for (const query of queries) {
            if (query.queryId && query.state === QueryState.RUNNING) {
                store.dispatch(doCancelQuery({ id: query.queryId }));
            }
            store.dispatch(doDeleteExploreQuery({ queryUuid: query.meta.queryUuid }));
        }
    }
});

const doRestoreQuery = (queryId, query) => ({
    type: RESTORE_QUERY,
    payload: { queryId, query }
});

const doResumeQuery = (queryId, queryKey, meta) => ({
    type: RESUME_QUERY,
    queryKey,
    getSqlParams: { queryId },
    meta
});

export const doListSnowflakeQueries = () => ({
    type: LIST_QUERIES,
    api: { ...LIST_SNOWFLAKE_QUERIES_OPERATION, sync: true },
    onSucceeded: ({ dispatch, getState, data }) => {
        const { queries } = data;
        const state = getState();
        const storeQueries = selectQueries(state);

        for (const query of queries) {
            const queryKey = query._context.meta.queryKey;

            if (!queryKey) {
                continue;
            }

            const storeQuery = storeQueries[query.queryId];

            if (storeQuery?.state === QueryState.RUNNING) {
                dispatch(
                    doResumeQuery(query.queryId, query._context.meta.queryKey, query._context.meta)
                );
            } else if (query.isStillRunning) {
                dispatch(doRestoreQuery(query.queryId, query));
                dispatch(
                    doResumeQuery(query.queryId, query._context.meta.queryKey, query._context.meta)
                );
            }
        }
    }
});
